import { rootMargin } from '@helpers/animate';
import { API_URLS } from '@helpers/api';
import { withMotionMax } from '@hoc/withMotionMax';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import { useInView } from '@hooks/useInView';
import Button, { LinkButton } from '@stories/Components/Buttons/Button/Button';
import { getSystemIcon, SystemIcon } from '@helpers/icons';
import ResourceCard from '@stories/Components/Cards/ResourceCard/ResourceCard';
import Filters from '@stories/Components/Listings/Filters/Filters';
import LoadingIndicator from '@stories/Components/Misc/LoadingIndicator/LoadingIndicator';
import { Theme } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import qs from 'query-string';
import * as React from 'react';
import { FocusRing } from 'react-aria';
import { useFirstMountState, useMedia } from 'react-use';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import BreadCrumbs from '../Global/BreadCrumbs/BreadCrumbs';
import S from './ResourceListing.styles';
import { getSrcs } from '@helpers/image';
import { Device, until } from '@helpers/media';
import { useCookies } from 'react-cookie';
const ResourceCardListing = (props) => {
    const isFirstMount = useFirstMountState();
    const [ref, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    const [searchResults, setSearchResults] = React.useState(props.initialData.items);
    const imageSrc = props.searchImg ? getSrcs(props.searchImg, 130, 100, 3, 65 / 142) : null;
    const isMobile = useMedia(until(Device.Tablet), false);
    // Used to determine if the Usp bar is active in the header and adjust the padding accordingly
    const topPaddingDefault = isMobile ? 100 : 280;
    const [sentTopPadding, setTopPadding] = React.useState(topPaddingDefault);
    const cookieName = props.cookieName ?? 'notice-cookie';
    const [cookies] = useCookies([cookieName]);
    React.useEffect(() => {
        const headerUspElement = document.getElementById('headerUsp');
        if (headerUspElement) {
            setTopPadding(headerUspElement.getBoundingClientRect().height + topPaddingDefault);
        }
    }, [isMobile, cookies[cookieName]]);
    const [params, setParams] = useQueryParams(Object.assign({
        pageIndex: withDefault(NumberParam, 1),
        pageSize: withDefault(NumberParam, 9),
        sortBy: withDefault(StringParam, ''),
        searchVal: withDefault(StringParam, ''),
        docId: withDefault(NumberParam, props.pageContext?.documentId),
    }, ...props.initialData.filters.map((f) => ({
        [f.id]: withDefault(ArrayParam, Array.isArray(f.value) ? f.value : [f.value ?? '']),
    }))));
    const [searchValue, setSearchValue] = React.useState(params.searchVal || '');
    /**
     * Compares the current selected filters in params to the initial filter values from props.initialData.filters.
     * Returns true if any filter value has changed, otherwise false.
     */
    function haveFiltersChanged() {
        return props.initialData.filters.some((filter) => {
            const initial = Array.isArray(filter.value)
                ? filter.value
                : [filter.value ?? ''];
            const current = params[filter.id] ?? [];
            // Compare arrays shallowly
            if (initial.length !== current.length)
                return true;
            for (let i = 0; i < initial.length; i++) {
                if (initial[i] !== current[i])
                    return true;
            }
            return false;
        });
    }
    const api = useApi({
        initialData: props.initialData,
        url: `${API_URLS.resourceListing}?${qs.stringify(params)}&location=${props.location}`,
    });
    const filters = React.useMemo(() => {
        return api.data?.filters
            .filter((f) => f.options.length > 0)
            .map((f) => ({
            ...f,
            value: params[f.id],
            onChange: (value) => {
                if (value === '') {
                    setParams({ [f.id]: [value], pageIndex: 1 }, 'pushIn');
                    setSearchResults([]);
                }
                else {
                    const values = params[f.id];
                    if (values.includes(value)) {
                        setParams({ [f.id]: values.filter((v) => v !== value), pageIndex: 1 }, 'pushIn');
                        setSearchResults([]);
                    }
                    else {
                        setParams({ [f.id]: [...values, value], pageIndex: 1 }, 'pushIn');
                        setSearchResults([]);
                    }
                }
            },
            hideMobile: false,
            type: 'multi',
        }));
    }, [api.data?.filters]);
    React.useEffect(() => {
        !isFirstMount && api.data && setSearchResults([...searchResults, ...api.data.items]);
    }, [api.data?.items]);
    React.useEffect(() => {
        if (isFirstMount) {
            setSearchValue(params.search);
        }
    }, [params.search]);
    return (React.createElement(S.Wrapper, { "data-theme": props.baseTheme ?? Theme.Pear, id: props.anchorId },
        React.createElement(S.SearchBanner, { style: { '--paddingTop': `${sentTopPadding}px` } },
            React.createElement(S.SearchBannerContainer, { flush: "both", translations: props.translations },
                React.createElement(S.SearchBannerGrid, null,
                    props.bannerTag && (React.createElement(S.BannerTag, null,
                        React.createElement(S.BannerTagInner, null, props.bannerTag))),
                    React.createElement(S.SearchArea, null,
                        React.createElement(S.SearchInner, null,
                            React.createElement(S.SearchTitle, null, props.searchTitle),
                            React.createElement(S.Search, { onSubmit: (event) => {
                                    event.preventDefault();
                                    if (params.searchVal === searchValue)
                                        return;
                                    setParams({ searchVal: searchValue });
                                    setSearchResults([]);
                                } },
                                React.createElement("input", { "aria-label": "Resource Search", type: "text", value: searchValue, placeholder: props.searchPlaceholder, onChange: handleSearchChange }),
                                React.createElement("div", null,
                                    React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                                        React.createElement("button", { "aria-label": "Submit Resource Search" },
                                            getSystemIcon(SystemIcon.Search),
                                            ' ',
                                            React.createElement("span", null, props.translations?.['global.filters.search']))))),
                            React.createElement(S.PopularSearchArea, null,
                                React.createElement(S.PopularSearchTitle, null, props.translations?.['global.filters.popular']),
                                React.createElement(S.PopularSearchOptions, null, props.popularSearches?.map((search, index) => (React.createElement(Button, { key: index, "aria-label": `Search for ${search}`, buttonType: Theme.White, onClick: () => {
                                        setSearchValue(search);
                                        setParams({ searchVal: search });
                                        setSearchResults([]);
                                    }, icon: SystemIcon.Search, iconPosition: "left" }, search))))),
                            imageSrc && imageSrc.src && (React.createElement(S.SearchImgArea, null,
                                React.createElement("img", { src: imageSrc.src, srcSet: imageSrc.srcSet, alt: props.searchImg?.altText, role: props.searchImg?.altText ? undefined : 'presentation', loading: 'eager' }))))),
                    React.createElement(S.BannerContent, null,
                        React.createElement(S.Heading, null, props.heading),
                        props.intro && React.createElement(S.Intro, { dangerouslySetInnerHTML: { __html: props.intro } }),
                        props.bannerCta && props.bannerCta.url && (React.createElement(LinkButton, { buttonType: props.baseTheme ?? Theme.Pear, icon: props.bannerCta?.icon ?? SystemIcon.Download, href: props.bannerCta?.url, target: props.bannerCta?.target }, props.bannerCta?.text)),
                        props.bannerCta2 && props.bannerCta2.url && (React.createElement(S.ExtraLink, null,
                            React.createElement(LinkButton, { buttonType: props.baseTheme ?? Theme.Pear, icon: SystemIcon.ArrowRight, href: props.bannerCta2?.url, target: props.bannerCta2?.target }, props.bannerCta2?.text))))))),
        React.createElement(BreadCrumbs, { items: props.breadcrumbs?.items ?? [], ...props.breadcrumbs, translations: props.translations }),
        React.createElement(S.Container, { ref: ref, flush: "top", translations: props.translations, "data-animate": inView },
            React.createElement(Filters, { filters: filters, sortBy: props.sortBy
                    ? {
                        ...props.sortBy,
                        value: params.sortBy,
                        onChange: (value) => {
                            setParams({ sortBy: value, pageIndex: 1 }, 'pushIn'),
                                setSearchResults([]);
                        },
                    }
                    : undefined, clearFilters: () => {
                    setParams({ pageSize: params.pageSize, sortBy: params.sortBy, pageIndex: 1 }, 'replace'),
                        setSearchResults(haveFiltersChanged() ? [] : props.initialData.items);
                }, resultAmount: searchResults.length ?? 0, resultTotal: api.data?.totalItems ?? 0, resultType: props.translations?.['global.filters.resources'] ?? '', translations: props.translations }),
            React.createElement(S.ResultsGrid, null, searchResults.length > 0 ? (React.createElement(React.Fragment, null, searchResults.map((item) => (React.createElement(ResourceCard, { key: item.id, ...item }))))) : (React.createElement(S.NoResultsMsg, null, props.translations?.['global.listings.noresults']))),
            searchResults.length > 0 ? (React.createElement(React.Fragment, null, (api.data?.totalItems ?? 0) > searchResults.length && (React.createElement(Button, { buttonType: props.baseTheme ?? Theme.Pear, onClick: () => setParams({ pageIndex: params.pageIndex + 1 }, 'pushIn'), icon: api.status === 'loading' ? SystemIcon.Loader : SystemIcon.Plus }, props.translations?.['global.listings.loadmore'])))) : null,
            api.status === 'loading' && (React.createElement(LoadingIndicator, { label: props.translations?.['global.listings.loading'], layout: "fixed" })))));
    function handleSearchChange(event) {
        setSearchValue(event.target.value);
    }
};
export default withQueryParams(withMotionMax(ResourceCardListing));
